import type { BuildingBounds } from "building-types/building/buildingBounds";
import type { Building } from "building-types/building/buildingTypes";
import { aabb2ByPolyLine2s } from "geom/aabb2";
import { planeNewYZ } from "geom/plane";
import { convertRoofFaceInfoToFaceListArray, getHighestRoofPoint, getRoofsTopFacesByLevel } from "../elements/roofs";
import {
  getBottomWallElevation,
  getTopWallElevation,
  makeBuildingDimensionContent,
} from "../generationTools/generateBuildingBoundsTools";
import memo from "../utils/memo";

function select({
  roomsByLevel,
  rooms,
  porches,
  foundations,
  walls,
  levels,
  roofSystems: roofs,
  settings,
}: Pick<
  Building,
  "roomsByLevel" | "rooms" | "porches" | "foundations" | "walls" | "levels" | "roofSystems" | "settings"
>) {
  return {
    levels,
    roomsByLevel,
    rooms,
    porches,
    foundations,
    walls,
    roofs,
    datum: settings.planset.exteriorDimensionDatum,
  };
}

function generate(opts: ReturnType<typeof select>): {
  buildingBounds: BuildingBounds;
} {
  const building = {
    ...opts,
  };
  const aabbContent = makeBuildingDimensionContent(building);
  const aabb = aabb2ByPolyLine2s(aabbContent);

  const { minX, minY, maxX, maxY } = aabb ? aabb : { minX: 0, minY: 0, maxX: 0, maxY: 0 };

  const roofFaces = convertRoofFaceInfoToFaceListArray(getRoofsTopFacesByLevel(opts.roofs, opts.levels));
  const hightestRoofZ = getHighestRoofPoint(roofFaces, planeNewYZ(0, 0, 0));

  const minZ = getBottomWallElevation(opts.levels);
  const maxZ = Math.max(hightestRoofZ, getTopWallElevation(opts.levels));

  return {
    buildingBounds: {
      highestRoofPoint: hightestRoofZ,
      buildingBottomElevation: minZ,
      centroidX: (maxX + minX) / 2,
      centroidY: (maxY + minY) / 2,
      centroidZ: (maxZ + minZ) / 2,
      width: maxX - minX,
      depth: maxY - minY,
      height: maxZ - minZ,
      aabb: { minX, minY, minZ, maxX, maxY, maxZ },
    },
  };
}

export default memo(select, generate);
